import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Button, Tag } from "../../../../../design-system";
import { AdCampaignPlan, campaignPlanFields, emptyCampaignPlan } from "../../../../../types/AdCampaignPlan";
import { saveCampaignPlan, subscribeCampaignPlan } from "../../../../../database/community/adCampaignPlan";
import { Card, CardHint, Muted, Row, Spacer } from "../shared";
import Modal from "../Modal";

const Field = styled.label`
  display: grid; gap: 6px; font-size: var(--font-size-sm);
  textarea { width: 100%; padding: 10px; resize: vertical;
    border: 1px solid var(--color-border-subtle); border-radius: var(--radius-md);
    background: var(--color-surface); color: var(--color-text); font: inherit; }
`;
const Text = styled.p`white-space: pre-wrap; overflow-wrap: anywhere; margin: 6px 0 18px;`;

const CampaignPlanPanel: React.FC<{ communityId: string; onNavigate: (path: string) => void }> = ({ communityId, onNavigate }) => {
  const [plan, setPlan] = useState<AdCampaignPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [draft, setDraft] = useState<AdCampaignPlan | null>(null);
  const [viewing, setViewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => subscribeCampaignPlan(communityId, next => {
    setPlan(next); setLoading(false); setLoadError("");
  }, () => { setLoading(false); setLoadError("Could not load the campaign plan. Reload to try again."); }), [communityId]);

  const edit = () => { setDraft(plan ? { ...plan } : emptyCampaignPlan()); setError(""); setSaved(false); };
  const save = async () => {
    if (!draft) return;
    setSaving(true); setError("");
    try {
      await saveCampaignPlan(communityId, { ...draft, assetIds: Array.from(new Set(draft.assetIds.map(id => id.trim()).filter(Boolean))) }, draft.updatedAt);
      setDraft(null); setSaved(true);
    } catch (e) { setError(e instanceof Error ? e.message : "Could not save this plan. Try again."); }
    finally { setSaving(false); }
  };

  return <Card>
    <Row style={{ borderTop: "none", padding: 0, flexWrap: "wrap" }}>
      <div><strong>Campaign plan</strong><Muted>Keep your offer, audiences and ad assignments with your creative library.</Muted></div>
      <Spacer /><Tag variant="neutral" size="sm">Draft brief</Tag>
      {plan && <Button variant="ghost" size="sm" onClick={() => setViewing(true)}>View plan</Button>}
      <Button size="sm" disabled={loading || Boolean(loadError)} onClick={edit}>{plan ? "Edit plan" : "Add plan"}</Button>
    </Row>
    {loading ? <CardHint>Loading campaign plan…</CardHint> : loadError ? <p role="alert">{loadError}</p> : plan ? <>
      <h3>{plan.title}</h3><Text>{plan.offer}</Text>
      <Muted>{plan.mediaFolder ? `Creative folder: ${plan.mediaFolder}` : "No creative folder linked"} · {plan.assetIds.length} linked assets</Muted>
    </> : <CardHint>No plan saved yet. Add your campaign offer, ad-set audiences and the ads you want to test.</CardHint>}
    <CardHint>Saving a plan does not create or activate campaigns in Meta.</CardHint>
    {saved && <p role="status">Campaign plan saved.</p>}
    {viewing && plan && <Modal title={plan.title} onClose={() => setViewing(false)} width={880}>
      {campaignPlanFields.filter(([key]) => key !== "title").map(([key, label]) => plan[key] && <div key={key}><strong>{label}</strong><Text>{plan[key]}</Text></div>)}
      {plan.mediaFolder && <Button variant="ghost" onClick={() => { setViewing(false); onNavigate(`/media-studio/library?folder=${encodeURIComponent(plan.mediaFolder)}`); }}>Open creative folder</Button>}
    </Modal>}
    {draft && <Modal title={plan ? "Edit campaign plan" : "Add campaign plan"} onClose={() => { if (!saving) setDraft(null); }} width={880}>
      {campaignPlanFields.map(([key, label]) => <Field key={key}>{label}
        <textarea value={draft[key]} rows={key === "title" || key === "mediaFolder" ? 1 : 4}
          maxLength={key === "title" || key === "mediaFolder" ? 200 : 20000} disabled={saving}
          onChange={e => setDraft({ ...draft, [key]: e.target.value })} />
      </Field>)}
      {error && <p role="alert">{error}</p>}
      <Button disabled={saving} onClick={() => void save()}>{saving ? "Saving…" : "Save draft plan"}</Button>
    </Modal>}
  </Card>;
};

export default CampaignPlanPanel;
